import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { Button, Divider, Modal, Spin, Tooltip } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import { combineClasses } from "../../../../lib/utils/combineClasses";
import { ViewModeType } from "../../../../lib/types";
import { AggregationRulesViewTable } from "../../features/AggregationRulesViewTable/AggregationRulesViewTable";
import { useAppDispatch, useAppSelector } from "../../../..";
import {
  aggregationRulesActions,
  closeAggregationRule,
  createAggregationRules,
  fetchAggregationRule,
  fetchAggregationRuleVersions,
  updateAggregationRule,
} from "../../store/slice/aggregationRulesSlice";
import {
  checkIsLoading,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils";
import { AggregationRulesEdit } from "../../features/AggregationRulesEdit/AggregationRulesEdit";
import { useOfferContext } from "../../../../containers/OfferContainer/context/OfferContext";
import { AggregationRule } from "../../../../lib/types/AggregationRule";
import { getRuleConditionBody } from "../../../../containers/OfferContainer/utils/getOfferCreateBody/getRuleConditionBody";
import { AggregationRulePageButtons } from "./AggregationRulePageButtons/AggregationRulePageButtons";
import { parseRuleCondition } from "../../../../containers/OfferContainer/utils/parseRuleCondition";
import { toHttpFormat, ymdDateFormat } from "../../../../lib/utils/formatDate";
import { FormInfo } from "../../../../components/common/FormInfo/FormInfo";
import "./AggregationRulePage.less";

interface ComponentProps {
  className?: string;
  viewMode: ViewModeType;
}

export const AggregationRulePage = (props: ComponentProps) => {
  const { className, viewMode } = props;
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory<{ isProductDetails?: boolean }>();
  const searchParams = new URLSearchParams(location.search);
  const offerFromDate = searchParams.get("onDate")?.replace(" ", "+");
  const ruleFromDate = searchParams.get("onDateRule")?.replace(" ", "+");
  const {
    aggregationRulesForm,
    getNewAggregationRule,
    newRuleCondition,
    priceCreationAllowed,
    reloadOffer,
  } = useOfferContext();

  const [currentViewMode, setCurrentViewMode] = useState(viewMode);

  const { aggregationRule, aggregationRuleLoading } = useAppSelector(
    (state) => state.aggregationRules
  );

  const { offerVersionsNotForViewing } = useAppSelector((state) => state.offering);

  const { offerId, ruleId } = useParams<{ offerId: string; ruleId: string }>();

  // Загрузка правила
  useEffect(() => {
    const id = Number(ruleId);
    if (!isNaN(id) && ruleFromDate) {
      dispatch(fetchAggregationRule({ id, onDate: ruleFromDate }));
      dispatch(fetchAggregationRuleVersions(id));
    } else {
      dispatch(aggregationRulesActions.cleanAggregationRule());
      dispatch(aggregationRulesActions.cleanAggregationRuleVersions());
      if (isNaN(id)) {
        console.error("Отсутствует id группы трафика");
      }
      if (!ruleFromDate) {
        console.error("Отсутствует дата создания группы трафика");
      }
    }
  }, [ruleFromDate, ruleId]);

  const submitCreate = () => {
    aggregationRulesForm.validateFields().then((values) => {
      const createObj: AggregationRule<"createObj">[] =
        values.aggregationRules
          ?.filter(
            ({ aggName, periodTypeId, rangeFrom }) =>
              !!aggName && !!periodTypeId && rangeFrom !== undefined
          )
          .map(({ id, aggName, periodTypeId, rangeFrom, rangeTo, ruleCondition, ...period }) => {
            const offeringId = isNaN(Number(offerId)) ? 0 : Number(offerId);
            const fromDate = period.fromDate ? toHttpFormatStartOfDay(period.fromDate) : undefined;
            const toDate = period.toDate ? toHttpFormatEndOfDay(period.toDate) : undefined;
            const ruleObj: AggregationRule<"createObj"> = {
              aggName: aggName!,
              periodTypeId: periodTypeId!,
              rangeFrom: rangeFrom!,
              rangeTo,
              ruleCondition: getRuleConditionBody(ruleCondition),
              offeringId,
              fromDate,
              toDate,
            };
            return ruleObj;
          }) || [];
      // return console.log({ createObj });
      dispatch(createAggregationRules(createObj)).then(({ payload }) => {
        const [{ offeringId, id, fromDate }] = payload as AggregationRule<"view">[];
        // const onDate = fromDate.slice(0, 10);
        const createdRulePath = `/offer/${offeringId}/aggregation-rule/${id}?onDate=${fromDate}&onDateRule=${fromDate}`;
        setCurrentViewMode("view");
        if (moment(fromDate, ymdDateFormat).isSame(moment(offerFromDate, ymdDateFormat), "day")) {
          reloadOffer();
        }
        history.replace(createdRulePath);
      });
    });
  };

  const submitUpdate = (force: boolean) => {
    aggregationRulesForm.validateFields().then((values) => {
      const editRule = values.aggregationRules?.[0];
      if (!editRule) {
        return;
      }
      const { id, aggName, periodTypeId, rangeFrom, rangeTo, ruleCondition, ...period } = editRule;
      const offeringId = isNaN(Number(offerId)) ? 0 : Number(offerId);
      const aggRuleId = isNaN(Number(ruleId)) ? 0 : Number(ruleId);
      const fromDate = period.fromDate ? toHttpFormatStartOfDay(period.fromDate) : undefined;
      const toDate = period.toDate ? toHttpFormatEndOfDay(period.toDate) : undefined;
      const editObj: AggregationRule<"createObj"> = {
        aggName: aggName!,
        periodTypeId: periodTypeId!,
        rangeFrom: rangeFrom!,
        rangeTo: rangeTo!,
        ruleCondition: getRuleConditionBody(ruleCondition),
        offeringId,
        fromDate,
        toDate,
      };
      dispatch(
        updateAggregationRule({
          aggregationRule: editObj,
          ruleId: aggRuleId,
          fromDate,
          force,
        })
      ).then(({ payload }) => {
        const { offeringId, id, fromDate } = payload as AggregationRule<"view">;
        if (force) {
          dispatch(fetchAggregationRule({ id, onDate: fromDate }));
          dispatch(fetchAggregationRuleVersions(id));
          setCurrentViewMode("view");
          reloadOffer();
        } else {
          const updatedRulePath = `/offer/${offeringId}/aggregation-rule/${id}?onDate=${toHttpFormat(
            offerFromDate
          )}&onDateRule=${fromDate}`;
          history.replace(updatedRulePath);
          setCurrentViewMode("view");
        }
      });
    });
  };

  const hadleCancelEdit = () => {
    aggregationRulesForm.resetFields();
    setCurrentViewMode("view");
  };

  const handleSubmitUpdate = (force: boolean) => {
    const formFromDate: Moment | undefined = aggregationRulesForm.getFieldValue([
      "aggregationRules",
      "0",
      "fromDate",
    ]);
    if (!formFromDate) {
      console.error("Отсутствует дата создания группы трафика");
    } else if (formFromDate.isBefore(moment(), "day")) {
      Modal.confirm({
        title: "Вы вносите исправления старой датой. Вы подтверждаете изменение?",
        onOk: () => {
          submitUpdate(force);
        },
        okText: "Подтвердить ",
        cancelText: "Отмена",
        onCancel: () => {
          hadleCancelEdit();
        },
      });
    } else {
      submitUpdate(force);
    }
  };

  const submitClose = (closeToDate: Moment) => {
    const id = Number(ruleId);
    if (!isNaN(id)) {
      dispatch(
        closeAggregationRule({ fromDate: toHttpFormatEndOfDay(closeToDate), ruleId: id })
      ).then(() => {
        if (ruleFromDate) {
          dispatch(fetchAggregationRule({ id, onDate: ruleFromDate }));
          dispatch(fetchAggregationRuleVersions(id));
          setCurrentViewMode("view");
          reloadOffer();
        } else {
          console.error("Отсутствует дата создания группы трафика");
        }
      });
    } else {
      console.error("Отсутствует id группы трафика");
    }
  };

  const handleSubmitClose = () => {
    const closeToDate: Moment | undefined = aggregationRulesForm.getFieldValue([
      "aggregationRules",
      "0",
      "toDate",
    ]);
    if (!closeToDate) {
      console.error("Отсутствует дата закрытия группы трафика");
    } else if (closeToDate.isBefore(moment(), "day")) {
      Modal.confirm({
        title: "Вы вносите исправления старой датой. Вы подтверждаете изменение?",
        onOk: () => {
          submitClose(closeToDate);
        },
        okText: "Подтвердить ",
        cancelText: "Отмена",
        onCancel: () => {
          hadleCancelEdit();
        },
      });
    } else {
      submitClose(closeToDate);
    }
  };

  const backToOffer = () => {
    if (offerFromDate) {
      if (history.location.state?.isProductDetails) {
        history.goBack();
      } else {
        history.push(`/offer/${offerId}?onDate=${offerFromDate}`);
      }
    }
  };

  const pageTitle = useMemo<string>(() => {
    switch (currentViewMode) {
      case "view":
        return "Просмотр группы трафика";
      case "create":
        return "Создание группы трафика";
      case "edit":
        return "Редактирование группы трафика";
      case "createBasedOn":
        return "Создание группы трафика";
      case "close":
        return "Изменение даты закрытия группы трафика";
      case "addVersion":
        return "Добавление версии группы трафика";
      default:
        return "Просмотр группы трафика";
    }
  }, [currentViewMode]);

  // Исходные данные группы трафика
  useEffect(() => {
    const getAggregationRule = (): AggregationRule<"createForm"> | undefined => {
      const fromDate = aggregationRule?.fromDate ? moment(aggregationRule.fromDate) : undefined;
      const toDate = aggregationRule?.toDate ? moment(aggregationRule.toDate) : undefined;
      const rule: AggregationRule<"createForm"> = {
        aggName: aggregationRule?.aggName,
        ruleCondition: parseRuleCondition(aggregationRule?.ruleCondition),
        // Эти танцы с бубном нужны, т.к. в форме глюк - остаются значения rangeFrom и rangeTo предыдущего ввода
        // @ts-ignore
        rangeFrom: aggregationRule?.rangeFrom === undefined ? "" : aggregationRule.rangeFrom,
        // @ts-ignore
        rangeTo: aggregationRule?.rangeTo === undefined ? "" : aggregationRule.rangeTo,
        periodTypeId: aggregationRule?.periodTypeId,
        fromDate,
        toDate,
      };
      switch (currentViewMode) {
        case "view":
          return undefined;
        case "create":
          return getNewAggregationRule();
        case "edit":
          return rule;
        case "createBasedOn":
          rule.fromDate = undefined;
          return rule;
        case "close":
          return rule;
        case "addVersion":
          rule.fromDate = rule.fromDate?.add(1, "day");
          return rule;
        default:
          return undefined;
      }
    };
    const rule = getAggregationRule();
    if (rule) {
      aggregationRulesForm.setFieldsValue({
        aggregationRules: [rule],
      });
    }
  }, [currentViewMode, aggregationRule]);

  // const { isLatestVersion, isPastVersion } = useMemo(() => {
  //   const sortedVersions = [...aggregationRuleVersions].sort(sortByToDate);
  //   const ruleFromDate = aggregationRule?.fromDate;
  //   const isLatestVersion = (() => {
  //     if (!ruleFromDate || !sortedVersions.length) {
  //       return false;
  //     }
  //     const lastVersion = sortedVersions[0];
  //     return moment(ruleFromDate).isBetween(lastVersion.fromDate, lastVersion.toDate, "day", "[]");
  //   })();
  //   const isPastVersion = (() => {
  //     if (!ruleFromDate || !sortedVersions.length) {
  //       return false;
  //     }
  //     const actualVersion = sortedVersions.find(({ fromDate, toDate }) =>
  //       moment(ruleFromDate).isBetween(fromDate, toDate, "day", "[]")
  //     );
  //     if (!actualVersion) {
  //       return false;
  //     }
  //     return moment(actualVersion.toDate).isBefore(moment(), "day");
  //   })();
  //   return { isLatestVersion, isPastVersion };
  // }, [aggregationRule, aggregationRuleVersions]);

  const editAvailableDates = useMemo(() => {
    let fromDate: Moment | undefined;
    let toDate: Moment | undefined;
    if (aggregationRule?.fromDate) {
      fromDate = moment(aggregationRule.fromDate);
    }
    if (aggregationRule?.toDate) {
      toDate = moment(aggregationRule.toDate);
    }
    return { fromDate, toDate };
  }, [aggregationRule]);

  const createAvailableDates = useMemo(() => {
    let fromDate: Moment | undefined;
    let toDate: Moment | undefined;
    const hideTsError = offerVersionsNotForViewing.forEach((version) => {
      if (version.fromDate) {
        if (!fromDate) {
          fromDate = moment(version.fromDate);
        } else if (moment(version.fromDate).isBefore(fromDate)) {
          fromDate = moment(version.fromDate);
        }
      }
      if (version.toDate) {
        if (!toDate) {
          toDate = moment(version.toDate);
        } else if (moment(version.toDate).isAfter(toDate)) {
          toDate = moment(version.toDate);
        }
      }
    });
    return { fromDate, toDate };
  }, [offerVersionsNotForViewing]);

  return (
    <div className={combineClasses("aggregationRule-page", className)}>
      <h1 id="offering-params-title">
        <Tooltip
          title={
            history.location.state?.isProductDetails ? "Вернуться назад" : "К просмотру предложения"
          }
        >
          <LeftOutlined onClick={backToOffer} className="aggregationRule-page__backBtn" />
        </Tooltip>
        {pageTitle}
      </h1>
      <Divider className="aggregationRule-page__topDivider" />
      {["view", "edit", "addVersion", "close"].includes(currentViewMode) &&
        !history.location.state?.isProductDetails && (
          <AggregationRulePageButtons
            viewMode={currentViewMode}
            // isLatestVersion={isLatestVersion}
            // isPastVersion={isPastVersion}
            setViewMode={(mode) => {
              setCurrentViewMode(mode);
            }}
            handleSubmitUpdate={handleSubmitUpdate}
            priceCreationAllowed={priceCreationAllowed}
            handleSubmitClose={handleSubmitClose}
            hadleCancelEdit={hadleCancelEdit}
          />
        )}
      {currentViewMode === "view" && aggregationRule && (
        <Spin spinning={checkIsLoading(aggregationRuleLoading)}>
          <AggregationRulesViewTable
            aggregationRules={[aggregationRule]}
            isToggleableColumns={false}
          />
        </Spin>
      )}
      {currentViewMode === "create" && (
        <Spin spinning={checkIsLoading(aggregationRuleLoading)}>
          <AggregationRulesEdit
            aggregationRulesForm={aggregationRulesForm}
            getNewAggregationRule={getNewAggregationRule}
            availableFields={[
              "aggName",
              "periodTypeId",
              "rangeFrom",
              "rangeTo",
              "ruleCondition",
              "fromDate",
              "toDate",
            ]}
            requiredFields={["aggName", "rangeFrom", "periodTypeId", "fromDate"]}
            newRuleCondition={newRuleCondition}
            hideCollapse
            availableDates={createAvailableDates}
          />
        </Spin>
      )}
      {currentViewMode === "edit" && (
        <Spin spinning={checkIsLoading(aggregationRuleLoading)}>
          <AggregationRulesEdit
            aggregationRulesForm={aggregationRulesForm}
            getNewAggregationRule={getNewAggregationRule}
            availableFields={["aggName", "periodTypeId", "rangeFrom", "rangeTo", "ruleCondition"]}
            requiredFields={["aggName", "rangeFrom", "periodTypeId"]}
            newRuleCondition={newRuleCondition}
            hideCollapse
            availableDates={editAvailableDates}
          />
          <Divider className="aggregationRule-page__bottomDivider" />
        </Spin>
      )}
      {currentViewMode === "createBasedOn" && (
        <Spin spinning={checkIsLoading(aggregationRuleLoading)}>
          <AggregationRulesEdit
            aggregationRulesForm={aggregationRulesForm}
            getNewAggregationRule={getNewAggregationRule}
            availableFields={[
              "aggName",
              "periodTypeId",
              "rangeFrom",
              "rangeTo",
              "ruleCondition",
              "fromDate",
              "toDate",
            ]}
            requiredFields={["aggName", "rangeFrom", "periodTypeId", "fromDate"]}
            newRuleCondition={newRuleCondition}
            hideCollapse
            availableDates={createAvailableDates}
          />
          <Divider className="aggregationRule-page__bottomDivider" />
        </Spin>
      )}
      {currentViewMode === "addVersion" && aggregationRule && (
        <Spin spinning={checkIsLoading(aggregationRuleLoading)}>
          <AggregationRulesEdit
            aggregationRulesForm={aggregationRulesForm}
            getNewAggregationRule={getNewAggregationRule}
            availableFields={[
              "aggName",
              "periodTypeId",
              "rangeFrom",
              "rangeTo",
              "ruleCondition",
              "fromDate",
            ]}
            requiredFields={["aggName", "rangeFrom", "periodTypeId", "fromDate"]}
            newRuleCondition={newRuleCondition}
            hideCollapse
            availableDates={{
              fromDate: editAvailableDates.fromDate
                ? moment(editAvailableDates.fromDate).add(1, "day")
                : undefined,
              toDate: editAvailableDates.toDate,
            }}
          />
          <Divider className="aggregationRule-page__bottomDivider" />
          <AggregationRulesViewTable
            aggregationRules={[aggregationRule]}
            isToggleableColumns={false}
            displayStyleAsChild
          />
        </Spin>
      )}
      {currentViewMode === "close" && (
        <Spin spinning={checkIsLoading(aggregationRuleLoading)}>
          <AggregationRulesEdit
            aggregationRulesForm={aggregationRulesForm}
            getNewAggregationRule={getNewAggregationRule}
            availableFields={["toDate"]}
            requiredFields={["toDate"]}
            newRuleCondition={newRuleCondition}
            hideCollapse
            availableDates={{
              fromDate: editAvailableDates.fromDate
                ? moment(editAvailableDates.fromDate).add(1, "day")
                : undefined,
              toDate: editAvailableDates.toDate,
            }}
          />
          <Divider className="aggregationRule-page__bottomDivider" />
        </Spin>
      )}
      {["create", "createBasedOn"].includes(currentViewMode) && (
        <>
          <Divider className="aggregationRule-page__bottomDivider" />
          <div className="aggregationRule-page__createBtns">
            <Button onClick={backToOffer}>Отмена</Button>
            <Button type="primary" onClick={submitCreate}>
              Создать
            </Button>
          </div>
        </>
      )}
      {currentViewMode !== "view" && (
        <FormInfo getFormValues={() => aggregationRulesForm.getFieldsValue()} />
      )}
    </div>
  );
};
